import { useState, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { IProduct } from "$containers";
import { EDBIntegratedOrders } from "$types/EDBIntegratedOrders";

const persistCart = (
  setValue: (name: string, value: any, options?: object) => void,
  nextCart: any[]
) => {
  setValue("products", nextCart, {
    shouldDirty: true,
    shouldTouch: false,
    shouldValidate: false,
  });
};

export const useMaterialNeed = () => {
  const { watch, setValue, getValues } = useFormContext();

  const [productError, setProductError] = useState<string>("");
  const [showEmptyCartModal, setShowEmptyCartModal] = useState(false);
  const [product, setProduct] = useState<IProduct | undefined>(undefined);

  const cart: any[] = watch("products", []);
  const integratedOrders: EDBIntegratedOrders = watch("integratedOrders");
  const dockyardId: string = watch("dockyard");

  const handleClickProduct = useCallback((_product: IProduct) => {
    setProduct(_product);
  }, []);

  const addToCart = useCallback(
    (pID: string, qty: number, replace = true, completeProduct?: any) => {
      const currentCart: any[] = [...(getValues("products") || [])];
      const index = currentCart.findIndex(({ productID }) => productID === pID);

      if (index >= 0) {
        const nextQty = replace
          ? qty
          : Number(currentCart[index].qty || 0) + Number(qty || 0);
        if (nextQty <= 0) {
          persistCart(
            setValue,
            currentCart.filter((p) => p.productID !== pID)
          );
        } else {
          currentCart[index] = { ...currentCart[index], qty: nextQty };
          persistCart(setValue, currentCart);
        }
        return;
      }

      if (qty <= 0) return;

      currentCart.push(
        completeProduct
          ? { ...completeProduct, productID: pID, qty }
          : { productID: pID, qty }
      );
      persistCart(setValue, currentCart);
    },
    [getValues, setValue]
  );

  const addManyToCart = useCallback(
    (items: { productID: string; qty: number }[]) => {
      const currentCart: any[] = [...(getValues("products") || [])];

      items.forEach(({ productID, qty }) => {
        if (!productID || !qty) return;
        const index = currentCart.findIndex((p) => p.productID === productID);
        if (index >= 0) {
          currentCart[index] = {
            ...currentCart[index],
            qty: Number(currentCart[index].qty || 0) + Number(qty),
          };
        } else {
          currentCart.push({ productID, qty: Number(qty) });
        }
      });

      persistCart(setValue, currentCart);
    },
    [getValues, setValue]
  );

  const removeFromCart = useCallback(
    (product: any) => {
      const currentCart: any[] = getValues("products") || [];
      persistCart(
        setValue,
        currentCart.filter((p) => p.productID !== product)
      );
    },
    [getValues, setValue]
  );

  const setIntegratedOrders = useCallback(
    (integratedOrders: EDBIntegratedOrders) => {
      setValue("integratedOrders", integratedOrders);
    },
    [setValue]
  );

  const clearCart = useCallback(() => {
    setValue("products", []);
  }, [setValue]);

  const getTotalArticle = useCallback(() => {
    const integratedTotal = integratedOrders
      ? (integratedOrders?.orderProducts?.reduce(
          (acc, product) => product.quantity + acc,
          0
        ) ?? 0)
      : 0;
    const cartTotal = cart?.reduce((acc, product) => product.qty + acc, 0);
    return integratedTotal + cartTotal;
  }, [cart, integratedOrders]);

  const handleEmptyCart = useCallback(() => {
    clearCart();
    setIntegratedOrders({
      ...integratedOrders,
      orderProducts: [],
    });
    setShowEmptyCartModal(false);
  }, [clearCart, integratedOrders, setIntegratedOrders]);

  return {
    cart,
    integratedOrders,
    dockyardId,
    productError,
    setProductError,
    showEmptyCartModal,
    setShowEmptyCartModal,
    product,
    setProduct,
    handleClickProduct,
    addToCart,
    addManyToCart,
    removeFromCart,
    setIntegratedOrders,
    clearCart,
    getTotalArticle,
    handleEmptyCart,
  };
};
